import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_BADGE_KEY, roleBadge } from '../../lib/tenantAccess';
import { setLastTenantSlug } from '../../utils/lastTenant';
import { ChannelIcon } from '../inbox/ChannelIcon';
import { popoverOffset } from '../../lib/popoverPosition';
import type { Tenant, AllowedPage } from '../../types/dashboard';
import { useTranslation } from 'react-i18next';

/**
 * Header workspace switcher — replaces the legacy `TenantPageSelector`.
 *
 * **Design rules vs the old component:**
 *
 * 1. **Click-only, no hover dropdowns.** Single interaction pattern on
 *    both mobile and desktop. Old version did "hover-to-show-pages" on
 *    desktop (pixel-positioned via getBoundingClientRect) and click on
 *    mobile — three patterns to learn, brittle, keyboard-inaccessible.
 *
 * 2. **One dropdown, three semantic sections:**
 *      - Current-workspace actions (Inbox / Manage pages / Personas)
 *      - Page filter (sets `?page=` on the inbox URL — does NOT navigate)
 *      - Workspace switcher (only when 2+ tenants)
 *
 * 3. **Route-aware display.** Parses the current path to render
 *    "Acme · Inbox", "Acme · Manage pages", etc. Knows about the new
 *    `/:slug/inbox` route.
 *
 * 4. **Page filter does not navigate.** Clicking a page chip writes to
 *    `?page=<pageId>` via URL — Inbox's URL sync picks it up and applies
 *    as a filter. Operator stays in the inbox view; the list narrows.
 *
 * 5. **Tenant switch navigates** to `/:newSlug/inbox` (per the routing
 *    direction E ships). Drops any current page filter (would be invalid
 *    for the new tenant's pages anyway).
 *
 * 6. **Accessible:** keyboard navigation, ARIA roles, click-outside +
 *    Escape to close. Focus returns to the trigger button on close.
 */

interface Scope {
  surface: 'inbox' | 'personas' | 'reports' | 'unknown';
  tenantSlug?: string;
  pageId?: string;
}

function parseScope(pathname: string, search: URLSearchParams): Scope {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return { surface: 'unknown' };
  if (parts[0] === 'personas') return { surface: 'personas' };
  if (parts[0] === 'tenants') return { surface: 'unknown' };

  const tenantSlug = parts[0];
  if (parts.length === 1) return { surface: 'unknown', tenantSlug };
  if (parts[1] === 'inbox') {
    return { surface: 'inbox', tenantSlug, pageId: search.get('page') || undefined };
  }
  if (parts[1] === 'pages') return { surface: 'inbox', tenantSlug }; // /pages redirects to the inbox
  if (parts[1] === 'reports') return { surface: 'reports', tenantSlug };
  return { surface: 'unknown', tenantSlug };
}

export function WorkspaceSwitcher() {
  const { tenants, tenantPages } = useTenant();
  const { access, canIn } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Keep the panel inside the viewport. A fixed right/left anchor cropped it: with a short
  // workspace name the right-anchored 320px panel ran off the left edge of the screen.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = triggerRef.current;
      const panel = popoverRef.current;
      if (!trigger || !panel) return;
      const r = trigger.getBoundingClientRect();
      const offset = popoverOffset({ left: r.left, width: r.width }, panel.offsetWidth, document.documentElement.clientWidth);
      panel.style.left = `${offset}px`;
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  const scope = useMemo(
    () => parseScope(location.pathname, searchParams),
    [location.pathname, searchParams],
  );

  const currentTenant = useMemo(
    () => (scope.tenantSlug ? tenants?.find((t) => t.slug === scope.tenantSlug) : null) ?? null,
    [tenants, scope.tenantSlug],
  );
  const currentPages = useMemo<AllowedPage[]>(
    () => (currentTenant ? tenantPages[currentTenant.id] || [] : []),
    [currentTenant, tenantPages],
  );
  const filteredPage = useMemo(
    () =>
      scope.pageId
        ? currentPages.find((p) => p.pageId === scope.pageId) ?? null
        : null,
    [currentPages, scope.pageId],
  );

  // Close on Escape or outside-click. Focus restoration: return to trigger
  // button when popover closes.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  // ── Display text ────────────────────────────────────────────────────
  // Hidden entirely when there's no tenant context (selector page, etc).
  if (!currentTenant) return null;

  const surfaceLabel: Record<Scope['surface'], string> = {
    inbox: t('switcher.inbox'),
    personas: t('switcher.personas'),
    reports: t('switcher.reports'),
    unknown: '',
  };
  const displayText = filteredPage
    ? `${currentTenant.name} · ${filteredPage.pageName || filteredPage.pageId}`
    : surfaceLabel[scope.surface]
    ? `${currentTenant.name} · ${surfaceLabel[scope.surface]}`
    : currentTenant.name;

  // ── Handlers ───────────────────────────────────────────────────────
  const goToInbox = () => {
    setOpen(false);
    navigate(`/${currentTenant.slug}/inbox`);
  };
  const goToPersonas = () => {
    setOpen(false);
    navigate('/personas');
  };
  const goToReports = () => {
    setOpen(false);
    navigate(`/${currentTenant.slug}/reports`);
  };
  const setPageFilter = (pageId: string | null) => {
    setOpen(false);
    // Update the URL — InboxUrlSync picks up and applies to the filter.
    // If we're not currently on the inbox surface, navigate there first.
    if (scope.surface !== 'inbox') {
      const qs = pageId ? `?page=${encodeURIComponent(pageId)}` : '';
      navigate(`/${currentTenant.slug}/inbox${qs}`);
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (pageId) next.set('page', pageId);
    else next.delete('page');
    const qs = next.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  };
  const switchWorkspace = (tenant: Tenant) => {
    setOpen(false);
    setLastTenantSlug(tenant.slug);
    navigate(`/${tenant.slug}/inbox`);
  };

  // ── Render ─────────────────────────────────────────────────────────
  const otherTenants = (tenants || []).filter((t) => t.id !== currentTenant.id);
  const hasMultipleTenants = (tenants?.length ?? 0) > 1;
  const hasMultiplePages = currentPages.length > 1;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-2 sm:px-3 min-h-11 text-xs sm:text-sm font-medium text-ink hover:bg-surface-2 rounded-control border border-line transition-colors max-w-44 sm:max-w-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        title={displayText}
      >
        <span className="truncate flex-1 text-left">{displayText}</span>
        <svg
          className={`w-3.5 h-3.5 text-muted transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <>
          {/* Mobile backdrop */}
          <div
            className="sm:hidden fixed inset-0 bg-scrim z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <div
            ref={popoverRef}
            role="menu"
            className="absolute left-0 top-full mt-1 w-80 bg-surface border border-line rounded-card shadow-pop z-50 overflow-hidden"
          >
            {/* Section 1 — current workspace header + primary actions */}
            <div className="px-3 py-2 bg-surface-2 border-b border-line">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-wide font-semibold text-muted truncate">
                  {currentTenant.name}
                </p>
                {roleBadge(access, currentTenant.id) && (
                  <span className="flex-shrink-0 text-xs font-medium text-muted bg-surface border border-line rounded-control px-1.5 py-0.5">
                    {t(ROLE_BADGE_KEY[roleBadge(access, currentTenant.id)!])}
                  </span>
                )}
              </div>
            </div>
            <ul className="py-1">
              <ActionItem
                active={scope.surface === 'inbox' && !filteredPage}
                onClick={goToInbox}
                label={t('switcher.viewAll')}
                hint={t('switcher.inboxHint')}
              />
              {canIn('viewReports', currentTenant.id) && (
                <ActionItem
                  active={scope.surface === 'reports'}
                  onClick={goToReports}
                  label={t('switcher.reports')}
                  hint={t('switcher.reportsHint')}
                />
              )}
              <ActionItem
                active={scope.surface === 'personas'}
                onClick={goToPersonas}
                label={t('switcher.myPersonas')}
              />
            </ul>

            {/* Section 2 — page filter (only when multi-page tenant) */}
            {hasMultiplePages && (
              <>
                <SectionHeader label={t('switcher.filterByPage', { count: currentPages.length })} />
                <ul className="py-1 max-h-60 overflow-y-auto">
                  <FilterItem
                    active={scope.surface === 'inbox' && !filteredPage}
                    onClick={() => setPageFilter(null)}
                    icon={null}
                    label={t('switcher.allPages')}
                  />
                  {currentPages.map((p) => (
                    <FilterItem
                      key={`${p.channel}:${p.pageId}`}
                      active={filteredPage?.pageId === p.pageId}
                      onClick={() => setPageFilter(p.pageId)}
                      icon={<ChannelIcon channel={p.channel} className="w-3.5 h-3.5" />}
                      label={p.pageName || p.pageId}
                      hint={p.collecting === false ? t('switcher.notCollecting') : undefined}
                    />
                  ))}
                </ul>
              </>
            )}

            {/* Section 3 — workspace switcher (only when 2+ tenants) */}
            {hasMultipleTenants && (
              <>
                <SectionHeader label={t('switcher.switchWorkspace', { count: otherTenants.length })} />
                <ul className="py-1 max-h-60 overflow-y-auto">
                  {otherTenants.map((ten) => {
                    const pages = tenantPages[ten.id] || [];
                    return (
                      <li key={ten.id}>
                        <button
                          role="menuitem"
                          onClick={() => switchWorkspace(ten)}
                          className="w-full text-left px-3 min-h-11 text-sm hover:bg-surface-2 transition-colors flex items-center justify-between gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <span className="truncate text-ink">{ten.name}</span>
                          <span className="text-xs text-muted flex-shrink-0">
                            {pages.length > 0 ? t('common.pages', { count: pages.length }) : t('switcher.noPages')}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-3 py-1.5 bg-surface-2 border-t border-b border-line">
      <p className="text-xs uppercase tracking-wide font-semibold text-muted">{label}</p>
    </div>
  );
}

function ActionItem({
  active,
  onClick,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint?: string;
}) {
  return (
    <li>
      <button
        role="menuitem"
        onClick={onClick}
        className={`w-full text-left px-3 min-h-11 text-sm transition-colors flex items-center justify-between gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
          active ? 'bg-tint text-on-tint font-medium' : 'text-ink hover:bg-surface-2'
        }`}
      >
        <span className="truncate">{label}</span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {hint && <span className="text-xs text-muted">{hint}</span>}
          {active && (
            <svg className="w-4 h-4 text-on-tint" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z" />
            </svg>
          )}
        </div>
      </button>
    </li>
  );
}

function FilterItem({
  active,
  onClick,
  icon,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode | null;
  label: string;
  /** e.g. "Not collecting" for pages whose messages Messaging doesn't store. */
  hint?: string;
}) {
  return (
    <li>
      <button
        role="menuitem"
        onClick={onClick}
        className={`w-full text-left px-3 min-h-11 text-sm transition-colors flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
          active ? 'bg-tint text-on-tint font-medium' : 'text-ink hover:bg-surface-2'
        }`}
      >
        {icon}
        <span className="truncate flex-1">{label}</span>
        {hint && (
          <span className="flex-shrink-0 rounded-control bg-surface-2 px-1.5 py-0.5 text-xs font-medium text-muted">{hint}</span>
        )}
        {active && (
          <svg className="w-4 h-4 text-on-tint flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z" />
          </svg>
        )}
      </button>
    </li>
  );
}
